import { apiUrl } from './api-base.js';
import { normalizeAssetUrl } from './asset-url.js';

const PRIVATE_IP_RE = /^(?:10\.|127\.|169\.254\.|192\.168\.|172\.(?:1[6-9]|2\d|3[01])\.)/;
const PROXY_HOST_SUFFIXES = ['ulucu.com', 'apac-ulucu.com', 'myqcloud.com'];

export interface ResolvedExternalMediaUrl {
	raw: string;
	primary: string;
	fallback: string;
}

export function resolveExternalImageUrl(value: unknown): ResolvedExternalMediaUrl {
	const raw = normalizeImageCandidate(value);
	if (!raw) return { raw: '', primary: '', fallback: '' };
	if (/^(?:data:image\/|blob:)/i.test(raw)) return { raw, primary: raw, fallback: '' };
	if (!/^https?:\/\//i.test(raw)) return { raw, primary: raw, fallback: '' };

	const primary = raw.replace(/^http:\/\//i, 'https://');
	const fallback = isProxyableMediaTarget(raw)
		? apiUrl(`/api/stream?url=${encodeURIComponent(raw)}`)
		: '';
	return { raw, primary, fallback };
}

export function normalizeExternalImageUrl(value: unknown): string {
	return resolveExternalImageUrl(value).primary;
}

export function normalizeImageCandidate(value: unknown): string {
	if (Array.isArray(value)) {
		for (const item of value) {
			const candidate = normalizeImageCandidate(item);
			if (candidate) return candidate;
		}
		return '';
	}
	if (value && typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const keys = ['url', 'src', 'pic_url', 'pic_path', 'img_url', 'image_url', 'thumbnail', 'thumb', 'path'];
		for (const key of keys) {
			const candidate = normalizeImageCandidate(record[key]);
			if (candidate) return candidate;
		}
		return '';
	}
	if (typeof value !== 'string') return '';
	const text = value.trim();
	if (!text) return '';
	const htmlImage = text.match(/<img\b[^>]*\bsrc=["']([^"']+)["']/i);
	if (htmlImage?.[1]) return normalizeImageCandidate(htmlImage[1]);
	if (/^\/\//.test(text)) return `https:${text}`;
	return normalizeAssetUrl(text);
}

export function isProxyableMediaTarget(raw: string): boolean {
	let url: URL;
	try {
		url = new URL(raw);
	} catch {
		return false;
	}
	if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
	const host = url.hostname.toLowerCase();
	if (PROXY_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))) {
		return true;
	}
	const isIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
	return isIPv4 && !PRIVATE_IP_RE.test(host);
}
